import { type InputHTMLAttributes, type ReactNode, type RefObject, forwardRef, useEffect, useId, useRef } from 'react';

import { cn } from '../utils/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Label for the checkbox
   */
  label?: ReactNode;

  /**
   * Description text below the label
   */
  description?: string;

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Checkbox size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Indeterminate state
   * @default false
   */
  indeterminate?: boolean;

  /**
   * Custom class for the container
   */
  containerClassName?: string;

  /**
   * Custom class for the label
   */
  labelClassName?: string;
}

/**
 * Checkbox component
 *
 * A checkbox input with label, description, and error state support.
 * Supports indeterminate state and multiple sizes.
 *
 * @example
 * ```tsx
 * <Checkbox label="Accept terms and conditions" />
 *
 * <Checkbox
 *   label="Subscribe to newsletter"
 *   description="Get weekly updates about new features"
 *   checked={true}
 * />
 *
 * <Checkbox
 *   label="Select all"
 *   indeterminate={true}
 * />
 * ```
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      size = 'md',
      indeterminate = false,
      disabled,
      className,
      containerClassName,
      labelClassName,
      id,
      ...props
    },
    ref
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const checkboxRef = (ref as RefObject<HTMLInputElement>) || internalRef;

    // Handle indeterminate state
    useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate, checkboxRef]);

    const sizeStyles = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    const labelSizeStyles = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    };

    const descriptionSizeStyles = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };

    const baseStyles = [
      'rounded border-2 bg-white dark:bg-gray-800',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 focus:ring-primary-500',
      'disabled:opacity-50 disabled:cursor-not-allowed',
    ];

    const colorStyles = error
      ? [
          'border-error-500 text-error-600 dark:text-error-400',
          'checked:bg-error-600 checked:border-error-600',
          'hover:border-error-600',
        ]
      : [
          'border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400',
          'checked:bg-primary-600 dark:checked:bg-primary-500 checked:border-primary-600 dark:checked:border-primary-500',
          'hover:border-primary-500 dark:hover:border-primary-400',
          'indeterminate:bg-primary-600 dark:indeterminate:bg-primary-500 indeterminate:border-primary-600 dark:indeterminate:border-primary-500',
        ];

    const autoId = useId();
    const generatedId = id || `checkbox-${autoId}`;

    const checkboxElement = (
      <input
        ref={checkboxRef}
        type="checkbox"
        id={generatedId}
        disabled={disabled}
        className={cn('cursor-pointer', baseStyles, colorStyles, sizeStyles[size], className)}
        {...props}
      />
    );

    if (!label && !description) {
      return checkboxElement;
    }

    return (
      <div className={cn('flex items-start', containerClassName)}>
        <div className="flex items-center h-6">{checkboxElement}</div>
        {(label || description) && (
          <div className="ml-3">
            {label && (
              <label
                htmlFor={generatedId}
                className={cn(
                  'font-medium',
                  labelSizeStyles[size],
                  disabled
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-900 dark:text-gray-100 cursor-pointer',
                  error && 'text-error-700 dark:text-error-400',
                  labelClassName
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p
                className={cn(
                  'text-gray-500 dark:text-gray-400',
                  descriptionSizeStyles[size],
                  disabled && 'text-gray-400 dark:text-gray-600',
                  error && 'text-error-600 dark:text-error-400'
                )}
              >
                {description}
              </p>
            )}
            {error && (
              <p
                className={cn(
                  'text-error-600 dark:text-error-400 mt-1',
                  descriptionSizeStyles[size]
                )}
              >
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
